<script lang="ts" setup>
import PublicLayout from "@/layouts/PublicLayout.vue";
import { useForm } from "@/composables";
import Banner from "./Partials/Banner.vue";
import { router } from "@inertiajs/vue3";
import { watch } from "vue";
import { cn } from "@/lib/utils";
import { computed } from "vue";
import VContainer from "@/components/VContainer.vue";
import { SelectNative } from "@/components/ui/select-native";

defineOptions({ layout: PublicLayout });

interface PageProps {
  event: App.Domain.Events.Http.Resources.EventResource & { fields: any[] };
  places: App.Domain.Events.Http.Resources.PlaceResource[];
  vehicles: App.Domain.Events.Http.Resources.VehicleResource[];
  data: App.Domain.Events.Http.Requests.BookingStoreRequest;
  tickets: any[];
  countries: any;
  hours: any[];
  days: any[];
  filters: any;
  privacy: any;
  terms: any;
  hasCoupons: boolean;
  variants: any[];
}

let scale = 0.75;

const props = defineProps<PageProps>();

const form = useForm<any>("post", route("events.bookings.store", props.event), {
  ...props.data,
});

watch(
  () => form.placeId,
  () =>
    router.reload({
      data: { placeId: form.placeId },
    }),
);

watch(
  () => form.vehicleId,
  () =>
    router.reload({
      data: { vehicleId: form.vehicleId },
    }),
);

function selectAvailability(disp) {
  if (isFull(disp) || disp.type != "online") return;

  if (form.spot == disp.id) {
    form.spot = null;
  } else {
    form.spot = disp.id;
  }
}

function isFull(disp) {
  return bookCount(disp) >= disp.capacity;
}

function bookCount(disp) {
  return disp.booked + (form.spot == disp.id ? 1 : 0);
}

const currentTicket = computed(() => props.tickets.find((t) => t.id == form.ticketId));
</script>

<template>
  <Banner
    :title="event.name"
    :image="event.banner_url"
    :payoff="event.payoff"
    class="mb-12"
  />

  <VContainer class="mb-12">
    <div
      class="prose max-w-none"
      v-html="event.description"
    ></div>
  </VContainer>

  <VContainer
    v-if="!event.bookable"
    class="text-center py-16 text-gray-600"
  >
    <div>
      <div class="shadow-none border-none text-lg">Evento chiuso - non si accettano più iscrizioni.</div>
    </div>
  </VContainer>

  <VContainer v-else>
    <Form
      @submit="form.submit()"
      class="flex flex-col gap-8"
    >
      <div v-for="variant in variants">
        <template v-if="variant.type == 'place'">
          <div v-if="variant.options.length > 0">
            <p class="font-semibold mb-4">Seleziona il luogo</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <button
                v-for="place in places"
                :key="place.id"
                :value="`${place.id}`"
                @click="form.placeId = place.id"
                class="border rounded text-sm text-left border-gray-200 w-full p-2"
                :class="{ 'skin-button': form.placeId == place.id }"
                type="button"
              >
                <span class="text-base font-medium">{{ place.name }}</span>
                <br />
                {{ [place.address, place.zip, place.city].join(", ") }}
              </button>
            </div>
          </div>
        </template>
        <template v-if="variant.type == 'model'">
          <div v-if="variant.options.length > 0">
            <p class="font-semibold mb-4">Seleziona un modello</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <button
                v-for="vehicle in variant.options"
                :key="vehicle.id"
                :value="`${vehicle.id}`"
                @click="
                  () => {
                    form.spot = null;
                    form.vehicleId = vehicle.id;
                  }
                "
                class="border rounded text-sm border-gray-200 w-full p-2 flex justify-between"
                :class="{ 'skin-button': form.vehicleId == vehicle.id }"
                type="button"
              >
                {{ vehicle.name }}
              </button>
            </div>
          </div>
        </template>
        <template v-if="variant.type == 'schedule'">
          <div v-if="event.type != 'registration' && variant.options.length > 0">
            <div class="mt-5 flex w-full h-full overflow-x-auto overflow-y-hidden divide-x">
              <div class="flex flex-col w-12 pl-4 sm:pl-0 mr-2 sm:mr-0">
                <div class="h-8 mb-2"></div>
                <div
                  v-for="hour in hours"
                  class="flex flex-shirnk-0 min-h-[45px] text-gray-800 items-start justify-center space-x-2 relative"
                >
                  <div class="text-sm pr-1 transform text-gray-400 -translate-y-1/2 bg-white">
                    {{ hour.name }}
                  </div>
                </div>
              </div>
              <div
                v-for="day in variant.options.filter((d) => !d.booked >= d.capacity)"
                class="flex flex-col w-full min-w-[calc(100vw-100px)] sm:min-w-72 max-w-96 px-2"
              >
                <h2 class="text-sm skin-typography-accent font-semibold border-b mb-2 p-1">
                  {{ day.name }} {{ " " }}<span class="text-gray-500 font-normal">{{ day.capacity ? ` | ${day.capacity} posto disponibile` : "" }}</span>
                </h2>
                <div class="relative">
                  <div
                    v-for="hour in hours"
                    class="absolute bg-gray-100 h-px inset-x-0"
                    :style="`top: calc(60px * ${hour.calc * scale})`"
                  ></div>
                  <button
                    v-for="disp in day.spots"
                    class="border rounded text-sm border-gray-200 w-full absolute items-center inset-x-0 p-2 flex justify-between flex-1"
                    :style="`height: ${disp.style.height * scale}px;top: ${disp.style.top * scale}px`"
                    @click="selectAvailability(disp)"
                    type="button"
                    :class="{ 'bg-gray-100 text-gray-600 cursor-not-allowed': isFull(disp), 'bg-white': !isFull(disp) }"
                  >
                    <template v-if="disp.type == 'online'">
                      <div class="flex items-center justify-start gap-2">
                        <input
                          type="radio"
                          v-if="form.spot == disp.id || !isFull(disp)"
                          :checked="form.spot == disp.id"
                          class="skin-accent"
                        />
                        <div>{{ disp.name }} {{ isFull(disp) ? " | completo" : " | libero" }}</div>
                      </div>
                      <div
                        v-if="!day.capacity"
                        class="whitespace-nowrap text-gray-500 tabular-nums"
                      >
                        <span>{{ bookCount(disp) }}/{{ disp.capacity }}</span>
                      </div>
                    </template>
                    <template v-else-if="disp.type == 'offline'">
                      <div class="flex items-center justify-start gap-2">
                        <Icon
                          name="MapPin"
                          class="size-4 -mx-[2px] text-gray-500 stroke-[1.5px]"
                        />
                        <div>{{ disp.name }} {{ " | Solo offline presso l'evento" }}</div>
                      </div>
                      <div></div>
                    </template>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div v-if="tickets.length > 0">
        <p class="font-semibold mb-4 text-lg">Seleziona</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <button
            v-for="ticket in tickets"
            :key="ticket.id"
            :value="`${ticket.id}`"
            @click="form.ticketId = ticket.id"
            class="border rounded text-sm text-left border-gray-200 w-full p-2 flex flex-col"
            :class="{ 'skin-button': form.ticketId == ticket.id }"
            type="button"
          >
            <strong class="mb-2">{{ ticket.name }}</strong>
            <strong class="mb-2">{{ ticket.price }}</strong>
            <div
              class="prose prose-sm"
              v-html="ticket.description"
            ></div>
          </button>
        </div>
      </div>

      <div
        v-if="currentTicket && currentTicket.fields.length > 0"
        class="px-6 flex flex-col gap-4"
      >
        <p class="font-semibold mb-4 text-lg">Compila</p>
        <FormInput
          v-for="field in currentTicket.fields"
          :label="field.name"
        >
          <template v-if="field.type == 'dropdown'">
            <SelectNative
              v-model="form.fields[field.handle]"
              :required="field.required"
            >
              <option
                v-for="option in field.options"
                :key="option.handle"
                :value="option.handle"
              >
                {{ option.label }}
              </option>
            </SelectNative>
          </template>
          <template v-if="field.type == 'text'">
            <Input
              :required="field.required"
              v-model="form.fields[field.handle]"
            />
          </template>
        </FormInput>
      </div>

      <div v-if="hasCoupons">
        <div class="mb-4">
          <h3 class="font-semibold text-lg">Hai un codice sconto?</h3>
          <p v-if="event.coupon_description">{{ event.coupon_description }}</p>
        </div>
        <FormInput
          label="Coupon"
          :errors="form.errors.coupon"
        >
          <Input
            v-model="form.coupon"
            type="text"
            placeholder="Codice..."
          />
        </FormInput>
      </div>

      <div class="shadow-none border-none pt-0">
        <div class="text-lg mb-4 font-semibold">Iscriviti</div>
        <div>
          <ErrorMessage />
        </div>
        <div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Nome"
              :errors="form.errors['customer.first_name']"
            >
              <Input
                placeholder="Mario.."
                v-model="form.customer.first_name"
                @change="form.validate('customer.first_name')"
                autocomplete="given-name"
              />
            </FormInput>
            <FormInput
              label="Cognome"
              :errors="form.errors['customer.last_name']"
            >
              <Input
                placeholder="Rossi..."
                v-model="form.customer.last_name"
                @change="form.validate('customer.last_name')"
                autocomplete="family-name"
              />
            </FormInput>
            <FormInput
              label="Email"
              :errors="form.errors['customer.email']"
            >
              <Input
                placeholder="mario.rossi@example.com"
                v-model="form.customer.email"
                @change="form.validate('customer.email')"
                autocomplete="email"
              />
            </FormInput>
            <FormInput
              label="Telefono"
              :errors="form.errors['customer.phone']"
            >
              <Input
                placeholder="+39"
                v-model="form.customer.phone"
                @change="form.validate('customer.phone')"
                autocomplete="phone"
              />
            </FormInput>
            <FormInput
              label="Paese"
              :errors="form.errors['customer.state']"
            >
              <SelectNative
                v-model="form.customer.state"
                @change="form.validate('customer.state')"
                placeholder="Seleziona un paese"
              >
                <option
                  v-for="country in countries"
                  :value="country.name"
                >
                  {{ country.name }}
                </option>
              </SelectNative>
            </FormInput>
            <FormInput
              label="CAP"
              :errors="form.errors['customer.zip']"
            >
              <Input
                placeholder=""
                v-model="form.customer.zip"
                @change="form.validate('customer.zip')"
              />
            </FormInput>
          </div>
          <div
            v-if="event.fields.length > 0"
            class="mt-4 flex flex-col gap-4"
          >
            <FormInput
              v-for="field in event.fields"
              :label="field.name"
              :errors="form.errors['fields.'  + field.handle]"
            >
              <template v-if="field.type == 'dropdown'">
                <SelectNative
                  v-model="form.fields[field.handle]"
                  :required="field.required"
                >
                  <option
                    v-for="option in field.options"
                    :key="option.handle"
                    :value="option.handle"
                  >
                    {{ option.label }}
                  </option>
                </SelectNative>
              </template>
              <template v-if="field.type == 'text'">
                <Input
                  v-model="form.fields[field.handle]"
                  :required="field.required"
                />
              </template>
              <template v-if="field.type == 'media'">
                <InputFile
                  v-model="form.fields[field.handle]"
                  :required="field.required"
                />
              </template>
            </FormInput>
          </div>
          <div class="mt-4">
            <FormInput
              label="Ho letto e accetto le condizioni generali per la privacy."
              :after="true"
              :inline="true"
              :errors="form.errors.privacy"
            >
              <Checkbox
                v-model="form.privacy"
                @change="form.validate('privacy')"
              />
            </FormInput>
            <div class="text-sm text-gray-700">
              <a
                target="_blank"
                :href="privacy"
                class="text-blue-600 underline"
                >Informativa Privacy e Condizioni di Trattamento dei Dati.</a
              >
              <p>{{ terms }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="shadow-none border-none mb-10">
        <div>
          <Button
            variant="accent"
            type="submit"
            :disabled="form.processing"
            size="lg"
          >
            Invia
          </Button>
        </div>
      </div>
    </Form>
  </VContainer>
</template>

<style>
.prose div[data-youtube-video] {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  max-width: 100%;
  margin: 0 auto;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
